import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const POLICIES = [
  { slug: 'terms',          label: 'Terms of Service',      short: 'Terms' },
  { slug: 'privacy',        label: 'Privacy Policy',        short: 'Privacy' },
  { slug: 'cookies',        label: 'Cookie Policy',         short: 'Cookies' },
  { slug: 'acceptable-use', label: 'Acceptable Use Policy', short: 'Acceptable Use' },
  { slug: 'dmca',           label: 'DMCA & IP Policy',      short: 'DMCA' },
  { slug: 'refunds',        label: 'Refund Policy',         short: 'Refunds' },
];

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--text)', marginBottom: 12, letterSpacing: '-0.01em' }}>
        {title}
      </h2>
      <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.8 }}>
        {children}
      </div>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul style={{ paddingLeft: 20, margin: '8px 0', display: 'flex', flexDirection: 'column', gap: 5 }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7 }}>{item}</li>
      ))}
    </ul>
  );
}

function SubSection({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

// ── Policy content ────────────────────────────────────────────
const CONTENT = {

  terms: {
    title: 'Terms of Service',
    content: (
      <>
        <Section title="1. Acceptance of Terms">
          These Terms of Service ("Terms") govern your access to and use of CopyPasteGenius ("Service"), operated by CopyPasteGenius ("we," "us," or "our"). By creating an account, installing our Chrome extension, or otherwise using the Service, you agree to these Terms. If you do not agree, do not use the Service.
          <br/><br/>
          These Terms apply to all users worldwide, including users in the European Union, United Kingdom, California, and other jurisdictions with specific consumer protection laws.
        </Section>
        <Section title="2. Description of Service">
          CopyPasteGenius is a software-as-a-service (SaaS) platform and Chrome browser extension that allows users to save, organize, and manage AI prompts and outputs from third-party AI tools. The Service includes:
          <BulletList items={[
            'A web application accessible at copypastegenius.com',
            'A Google Chrome browser extension',
            'An API for programmatic access',
            'Free and paid subscription tiers with differing feature sets',
          ]}/>
        </Section>
        <Section title="3. Account Registration">
          To use the Service, you must create an account using a valid email address or supported OAuth provider. You agree to provide accurate and complete information and to keep your account credentials confidential. You are responsible for all activity that occurs under your account.
          <br/><br/>
          You must be at least 13 years of age (or 16 in the EU/UK) to create an account. By registering, you represent that you meet the applicable minimum age requirement.
        </Section>
        <Section title="4. Subscription Plans and API Keys">
          <SubSection title="4.1 Free Plan">
            The Free plan provides basic access to the Service with limitations on storage, features, and usage as described on our pricing page.
          </SubSection>
          <SubSection title="4.2 Paid Plans">
            Paid subscriptions unlock additional features including private entries and increased storage. Subscription fees are billed in advance on a monthly or annual basis. You authorize us to charge your payment method on a recurring basis.
          </SubSection>
          <SubSection title="4.3 API Keys">
            API keys are issued per account and used to authenticate requests from the Chrome extension and third-party integrations. You are solely responsible for maintaining the confidentiality of your API key. We reserve the right to revoke API keys that are misused or shared publicly.
          </SubSection>
        </Section>
        <Section title="5. Acceptable Use">
          You agree not to use the Service to:
          <BulletList items={[
            'Violate any applicable law or regulation',
            'Upload, store, or share content that infringes any intellectual property rights',
            'Transmit malware, viruses, or other harmful code',
            'Scrape or extract data from third-party AI platforms in violation of their terms of service',
            'Circumvent any security or access control mechanism of the Service',
            'Resell, sublicense, or redistribute the Service without written permission',
            'Generate, store, or distribute illegal, harmful, or offensive content',
          ]}/>
        </Section>
        <Section title="6. Content and Intellectual Property">
          <SubSection title="6.1 Your Content">
            You retain ownership of all prompts, outputs, and other content you upload or save through the Service ("User Content"). By using the Service, you grant us a limited, non-exclusive, worldwide license to store, process, and transmit your User Content solely as necessary to provide the Service.
          </SubSection>
          <SubSection title="6.2 Public Content">
            If you mark content as public, you grant other users a non-exclusive license to view that content. You represent that you have the rights necessary to make content public.
          </SubSection>
          <SubSection title="6.3 Our Intellectual Property">
            All rights, title, and interest in the Service, including its software, design, trademarks, and documentation, are owned by CopyPasteGenius. Nothing in these Terms grants you any right to use our intellectual property except as expressly permitted.
          </SubSection>
        </Section>
        <Section title="7. Third-Party AI Platforms">
          The Service integrates with third-party AI platforms (including but not limited to ChatGPT, Claude, and Gemini) to capture content. We are not affiliated with these platforms. You are solely responsible for ensuring your use of the Chrome extension complies with the terms of service of any third-party platform from which you capture content.
        </Section>
        <Section title="8. Disclaimers and Limitation of Liability">
          THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          <br/><br/>
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM, OR (B) USD $100. Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be limited under applicable law.
        </Section>
        <Section title="9. Indemnification">
          You agree to indemnify and hold harmless CopyPasteGenius and its officers, directors, employees, and agents from any claims, damages, losses, and expenses (including reasonable legal fees) arising from your use of the Service, your violation of these Terms, or your infringement of any third-party rights.
        </Section>
        <Section title="10. Termination">
          We may suspend or terminate your account at any time for violation of these Terms or applicable law, with or without notice. You may cancel your account at any time through account settings. Upon termination, your right to access the Service ceases immediately.
        </Section>
        <Section title="11. Governing Law and Dispute Resolution">
          These Terms shall be governed by applicable law. For users in the European Union, nothing in these Terms shall deprive you of the mandatory protections of your local consumer laws. For US-based disputes, you agree to resolve disputes through binding arbitration in accordance with the rules of the American Arbitration Association, except where prohibited by law.
        </Section>
        <Section title="12. Changes to Terms">
          We may modify these Terms at any time. We will provide notice of material changes by email or by posting a notice on the Service at least 30 days before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Terms.
        </Section>
        <Section title="13. Contact">
          For questions about these Terms, contact us at: <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a>
        </Section>
      </>
    ),
  },

  privacy: {
    title: 'Privacy Policy',
    content: (
      <>
        <Section title="1. Who We Are">
          CopyPasteGenius ("we," "us," or "our") operates the website at copypastegenius.com and the CopyPasteGenius Chrome extension. We are the data controller for personal data processed through the Service.
          <br/><br/>
          Data Protection Contact: <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a>
        </Section>
        <Section title="2. Data We Collect">
          <SubSection title="2.1 Account Data">
            <BulletList items={[
              'Email address and display name (provided at registration)',
              'Authentication tokens (Google, GitHub OAuth if applicable)',
              'Subscription and payment status (not payment card details — processed by Stripe)',
            ]}/>
          </SubSection>
          <SubSection title="2.2 User Content">
            <BulletList items={[
              'AI prompts and outputs you save through the Service or Chrome extension',
              'Tags, titles, notes, and metadata you attach to saved entries',
              'Public/private visibility settings',
            ]}/>
          </SubSection>
          <SubSection title="2.3 Technical Data">
            <BulletList items={[
              'IP address and approximate geolocation (country/region)',
              'Browser type, operating system, and extension version',
              'API key usage logs and request timestamps',
              'Service usage analytics (features used, session duration)',
            ]}/>
          </SubSection>
          <SubSection title="2.4 Cookies and Tracking">
            We use cookies and similar technologies as described in our <Link to="/legal/cookies" style={{ color: 'var(--accent)' }}>Cookie Policy</Link>. We use Google Analytics to understand how users interact with the Service.
          </SubSection>
        </Section>
        <Section title="3. How We Use Your Data">
          We use your personal data to:
          <BulletList items={[
            'Provide, maintain, and improve the Service',
            'Authenticate your identity and manage your account',
            'Process payments and manage subscriptions',
            'Send transactional emails (account confirmations, password resets)',
            'Send service updates and marketing communications (with consent where required)',
            'Detect and prevent fraud, abuse, and security incidents',
            'Comply with legal obligations',
          ]}/>
        </Section>
        <Section title="4. Legal Bases for Processing (GDPR)">
          For users in the European Economic Area (EEA) and UK, our legal bases for processing are:
          <BulletList items={[
            'Contract: Processing necessary to provide the Service',
            'Legitimate Interests: Security, fraud prevention, and improving the Service',
            'Consent: Marketing emails and non-essential cookies (you may withdraw at any time)',
            'Legal Obligation: Compliance with applicable laws',
          ]}/>
        </Section>
        <Section title="5. Data Sharing and Third Parties">
          We share data only as necessary:
          <BulletList items={[
            'Supabase: Database, authentication, and storage infrastructure',
            'Stripe: Payment processing (they handle card data; we receive only billing status)',
            'Vercel: Web hosting infrastructure',
            'Google: OAuth authentication and Analytics',
            'Google Ads / advertising platforms: With your consent via cookie preferences',
          ]}/>
          We do not sell your personal data. We do not share your User Content with third parties.
        </Section>
        <Section title="6. International Data Transfers">
          Our infrastructure is hosted in the United States. If you are located outside the US, your data will be transferred to and processed in the US. For EEA/UK users, we ensure such transfers are made in accordance with applicable transfer mechanisms (e.g., Standard Contractual Clauses).
        </Section>
        <Section title="7. Data Retention">
          We retain your personal data for as long as your account is active. If you delete your account, we will delete or anonymize your personal data within 30 days, except where we are required to retain it for legal or compliance purposes.
        </Section>
        <Section title="8. Your Rights">
          Depending on your jurisdiction, you may have the following rights:
          <BulletList items={[
            'Access: Request a copy of your personal data',
            'Rectification: Correct inaccurate personal data',
            'Erasure: Request deletion of your personal data ("right to be forgotten")',
            'Portability: Receive your data in a structured, machine-readable format',
            'Restriction: Request that we limit processing of your data',
            'Objection: Object to processing based on legitimate interests',
            'Opt-out of sale (CCPA): We do not sell data, but you may submit this request at any time',
          ]}/>
          To exercise your rights, contact us at <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a>. We will respond within 30 days.
        </Section>
        <Section title="9. Children's Privacy">
          The Service is not directed to children under 13 (or 16 in the EEA/UK). We do not knowingly collect personal data from children. If we become aware that a child has provided us with personal data, we will delete it promptly.
        </Section>
        <Section title="10. Security">
          We implement industry-standard security measures including encryption in transit (TLS), encryption at rest, row-level security in our database, and API key authentication. However, no system is completely secure.
        </Section>
        <Section title="11. Changes to This Policy">
          We may update this Privacy Policy from time to time. We will notify you of material changes by email or by posting a notice on the Service.
        </Section>
        <Section title="12. Contact and Complaints">
          For privacy-related questions: <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a>
          <br/><br/>
          EEA/UK users have the right to lodge a complaint with their local data protection authority. A list of EU supervisory authorities can be found at <a href="https://edpb.europa.eu" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>edpb.europa.eu</a>.
        </Section>
      </>
    ),
  },

  cookies: {
    title: 'Cookie Policy',
    content: (
      <>
        <Section title="1. What Are Cookies?">
          Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work efficiently and to provide information to website owners. Similar technologies include local storage, session storage, and browser fingerprinting.
        </Section>
        <Section title="2. Cookies We Use">
          <SubSection title="2.1 Strictly Necessary Cookies">
            These cookies are essential for the Service to function and cannot be disabled.
            <BulletList items={[
              'Authentication session cookies (Supabase): maintain your login state',
              'Security cookies: protect against CSRF attacks',
              'Load balancing cookies: distribute traffic across servers',
            ]}/>
          </SubSection>
          <SubSection title="2.2 Functional Cookies">
            These cookies remember your preferences and settings.
            <BulletList items={[
              'Theme preference (light/dark mode)',
              'Language and regional preferences',
              'Dismissed notifications and onboarding state',
            ]}/>
          </SubSection>
          <SubSection title="2.3 Analytics Cookies">
            With your consent, we use Google Analytics to understand how users interact with the Service.
            <BulletList items={[
              'Page views, feature usage, and session duration',
              'Error and performance monitoring',
              'Aggregate audience demographics (no individual tracking across sites without consent)',
            ]}/>
          </SubSection>
          <SubSection title="2.4 Advertising Cookies">
            With your consent, we use advertising cookies to show you relevant ads and measure campaign performance.
            <BulletList items={[
              'Google Ads: conversion tracking and remarketing',
              'These cookies may track your activity across websites',
              'You can opt out via your cookie preferences or at g.co/adsettings',
            ]}/>
          </SubSection>
          <SubSection title="2.5 Chrome Extension Storage">
            The CopyPasteGenius Chrome extension uses chrome.storage.local (not cookies) to store your API key and extension preferences. This data is stored locally on your device and is never shared without your action.
          </SubSection>
        </Section>
        <Section title="3. Your Cookie Choices">
          When you first visit our website, you will be presented with a cookie consent banner. You may accept all cookies, reject non-essential cookies, or customize your preferences. You can change your preferences at any time by clicking "Cookie Preferences" in the footer.
          <br/><br/>
          You can also manage cookies through your browser settings. Note that disabling strictly necessary cookies may break core functionality of the Service.
        </Section>
        <Section title="4. Third-Party Cookies">
          Some third-party services we use may place their own cookies:
          <BulletList items={[
            'Google (OAuth sign-in and Analytics): subject to Google\'s cookie policy',
            'Stripe (payment processing): subject to Stripe\'s cookie policy',
            'Google Ads: subject to Google\'s advertising policies',
          ]}/>
          We do not use other third-party advertising cookies or tracking pixels beyond those described above.
        </Section>
        <Section title="5. Updates to This Policy">
          We may update this Cookie Policy when we change the cookies we use. We will notify you of material changes through the cookie consent banner or by email.
        </Section>
        <Section title="6. Contact">
          For questions about our use of cookies: <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a>
        </Section>
      </>
    ),
  },

  'acceptable-use': {
    title: 'Acceptable Use Policy',
    content: (
      <>
        <Section title="1. Purpose">
          This Acceptable Use Policy (AUP) supplements our <Link to="/legal/terms" style={{ color: 'var(--accent)' }}>Terms of Service</Link> and is designed to protect the integrity of the CopyPasteGenius platform and the interests of all users. It applies to all users, including API users and Chrome extension users.
        </Section>
        <Section title="2. Prohibited Activities">
          <SubSection title="2.1 Illegal Use">
            <BulletList items={[
              'Using the Service for any unlawful purpose or in violation of any local, national, or international law',
              'Storing or distributing content that violates intellectual property rights',
              'Facilitating or promoting illegal activity',
            ]}/>
          </SubSection>
          <SubSection title="2.2 Harmful Content">
            <BulletList items={[
              'Uploading or sharing content that is abusive, harassing, defamatory, or threatening',
              'Storing or distributing malware, viruses, or malicious code',
              'Content that exploits or harms minors',
            ]}/>
          </SubSection>
          <SubSection title="2.3 Platform Abuse">
            <BulletList items={[
              'Attempting to gain unauthorized access to any part of the Service or its infrastructure',
              'Using automated scripts or bots to abuse the API beyond reasonable use',
              'Sharing, selling, or publicly disclosing your API key',
              'Creating multiple accounts to circumvent usage limits or bans',
              'Interfering with the Service\'s availability or performance for other users',
            ]}/>
          </SubSection>
          <SubSection title="2.4 Third-Party Platform Violations">
            <BulletList items={[
              'Using the Chrome extension to violate the terms of service of any third-party AI platform',
              'Harvesting AI outputs for commercial redistribution without appropriate rights',
            ]}/>
          </SubSection>
        </Section>
        <Section title="3. API Usage Limits">
          API usage is subject to rate limits as specified in our documentation. Excessive usage that degrades service quality for others may result in temporary or permanent throttling. Commercial or high-volume use cases must be agreed upon in writing.
        </Section>
        <Section title="4. Enforcement">
          Violations of this AUP may result in warnings, temporary suspension, or permanent account termination, at our sole discretion. We may also report illegal activity to appropriate authorities.
          <br/><br/>
          To report a violation, contact <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a>.
        </Section>
      </>
    ),
  },

  dmca: {
    title: 'DMCA & Intellectual Property Policy',
    content: (
      <>
        <Section title="Overview">
          CopyPasteGenius respects intellectual property rights and expects its users to do the same. This policy explains how we handle copyright infringement claims under the Digital Millennium Copyright Act (DMCA) and equivalent international laws.
        </Section>
        <Section title="1. DMCA Takedown Procedure">
          If you believe that content on CopyPasteGenius infringes your copyright, you may submit a DMCA takedown notice to our designated agent. Your notice must include:
          <BulletList items={[
            'Your name, address, telephone number, and email address',
            'Identification of the copyrighted work you claim has been infringed',
            'Identification of the infringing material and its URL on our platform',
            'A statement that you have a good faith belief that the use is not authorized',
            'A statement that the information in your notice is accurate and, under penalty of perjury, that you are authorized to act on behalf of the copyright owner',
            'Your physical or electronic signature',
          ]}/>
          Submit DMCA notices to: <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a> (Subject: "DMCA Takedown Notice")
        </Section>
        <Section title="2. Counter-Notice Procedure">
          If your content was removed in response to a DMCA notice and you believe it was removed in error, you may submit a counter-notice. We will restore the content within 10–14 business days unless the complainant initiates legal action.
        </Section>
        <Section title="3. Repeat Infringers">
          We maintain a policy of terminating, in appropriate circumstances, the accounts of users who are repeat infringers of intellectual property rights.
        </Section>
        <Section title="4. International IP Rights">
          For copyright claims from jurisdictions outside the United States, we will evaluate claims made under equivalent national laws (e.g., EU Copyright Directive, UK CDPA) on a case-by-case basis.
        </Section>
      </>
    ),
  },

  refunds: {
    title: 'Refund Policy',
    content: (
      <>
        <Section title="1. Our Commitment">
          We want you to be satisfied with CopyPasteGenius. If the Service does not meet your needs, we offer refunds as described below.
        </Section>
        <Section title="2. Subscription Refunds">
          <SubSection title="2.1 New Subscriptions — 14-Day Money-Back Guarantee">
            If you subscribe to a paid plan for the first time and are not satisfied, you may request a full refund within 14 days of your initial payment. No questions asked. This guarantee applies to your first subscription period only.
          </SubSection>
          <SubSection title="2.2 Renewals">
            Renewals (monthly or annual) are generally non-refundable. However, if you were unaware of an upcoming renewal, you may request a refund within 7 days of the renewal charge, provided you have not materially used the Service during that renewal period.
          </SubSection>
          <SubSection title="2.3 Annual Plans — Pro-Rata Refunds">
            If you cancel an annual subscription after the 14-day guarantee period, you may request a pro-rata refund for the remaining unused full months, at our discretion. Partial months are not refunded.
          </SubSection>
        </Section>
        <Section title="3. Non-Refundable Items">
          The following are not eligible for refunds:
          <BulletList items={[
            'One-time add-ons or feature purchases',
            'Accounts suspended or terminated due to violations of our Terms of Service',
            'Subscriptions where fraudulent activity is suspected',
          ]}/>
        </Section>
        <Section title="4. EU/UK Statutory Rights">
          If you are a consumer in the European Union or United Kingdom, you may have a statutory right to cancel your subscription within 14 days of purchase (the "cooling-off period"). If you have already begun using the Service within the cooling-off period, we may charge a proportionate amount for the services used.
        </Section>
        <Section title="5. How to Request a Refund">
          To request a refund, email us at <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a> with the subject line "Refund Request" and include your registered email address, the date of purchase, and the reason for your request.
          <br/><br/>
          We will process eligible refunds within 5–10 business days. Refunds are issued to the original payment method.
        </Section>
        <Section title="6. Cancellation">
          Cancelling your subscription stops future charges but does not automatically trigger a refund. Your access continues until the end of the current paid period. To cancel, go to Account Settings → Billing → Cancel Subscription.
        </Section>
        <Section title="7. Contact">
          For billing and refund questions: <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a>
        </Section>
      </>
    ),
  },
};

export default function LegalPage({ slug }) {
  const policy = CONTENT[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font)' }}>
      {/* Header */}
      <div style={{ background: 'var(--surface)', borderBottom: '0.5px solid var(--border)', padding: '14px 24px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ fontSize: 16, fontWeight: 500, color: 'var(--text)', textDecoration: 'none', letterSpacing: '-0.03em' }}>
            CopyPaste<span style={{ color: '#7f77dd' }}>Genius</span>
          </Link>
          <Link to="/app" style={{ fontSize: 13, color: 'var(--muted)', textDecoration: 'none' }}>← Back to app</Link>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 24px', display: 'flex', gap: 48, alignItems: 'flex-start' }}>

        {/* Sidebar nav */}
        <div style={{ width: 200, flexShrink: 0, position: 'sticky', top: 80 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--dim)', marginBottom: 12, fontWeight: 500 }}>
            Legal
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {POLICIES.map(p => (
              <Link key={p.slug} to={`/legal/${p.slug}`} style={{
                fontSize: 13, padding: '7px 10px', borderRadius: 6, textDecoration: 'none',
                color: slug === p.slug ? '#7f77dd' : 'var(--muted)',
                background: slug === p.slug ? 'rgba(127,119,221,0.1)' : 'transparent',
                fontWeight: slug === p.slug ? 500 : 400,
                transition: 'all 0.12s',
              }}>{p.label}</Link>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {policy ? (
            <>
              <div style={{ marginBottom: 32 }}>
                <h1 style={{ fontSize: 28, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em', marginBottom: 8 }}>
                  {policy.title}
                </h1>
                <div style={{ fontSize: 13, color: 'var(--dim)' }}>
                  Effective Date: March 24, 2026 · <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--accent)' }}>legal@copypastegenius.com</a>
                </div>
              </div>
              {policy.content}
            </>
          ) : (
            <div style={{ fontSize: 15, color: 'var(--muted)' }}>Policy not found.</div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '0.5px solid var(--border)', padding: '24px', background: 'var(--surface)', marginTop: 40 }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', textAlign: 'center', fontSize: 12, color: 'var(--dim)' }}>
          © {new Date().getFullYear()} CopyPasteGenius · <a href="mailto:legal@copypastegenius.com" style={{ color: 'var(--dim)' }}>legal@copypastegenius.com</a>
        </div>
      </div>
    </div>
  );
}